import React from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ListChecks, Trophy, UserCheck, UserPlus, Users } from "lucide-react";
import { fetchExplorer, fetchExplorerPosts, followExplorer, unfollowExplorer } from "../../api/social";
import { useAuth } from "../../hooks/useAuth";
import { usePageMeta } from "../../hooks/usePageMeta";
import AppShell from "../../layouts/AppShell";
import { AtlasPanel } from "../../components/AtlasPanel";
import { Button } from "../../components/Button";
import { PostCard } from "../../components/PostCard";
import { ProfileIdentity } from "../../components/ProfileIdentity";
import { StatBlock } from "../../components/StatBlock";
import { AchievementBadge } from "../../components/AchievementBadge";
import { CollectionCard } from "../../components/CollectionCard";
import { Skel } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { GuestTopBar } from "../../components/GuestTopBar";
import { getBannerAsset } from "../../lib/cosmetics";
import { rarityGlowMd } from "../../lib/rarity";
import { iconByName as achievementIcon } from "../../lib/achievement-icons";

// ─── ExplorerProfilePage ──────────────────────────────────────────────────────
// Registered on the root route (not under _protected) so it's reachable while
// signed out. Renders inside <AppShell> when a session exists, otherwise as a
// standalone page — the shareable "trophy case" link for public profiles.

export default function ExplorerProfilePage() {
  const { username } = useParams({ strict: false }) as { username: string };
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: me, isLoading: meLoading } = useAuth();

  const explorerQuery = useQuery({
    queryKey: ["explorers", username],
    queryFn: () => fetchExplorer(username),
    retry: false,
  });

  const followMutation = useMutation({
    mutationFn: (following: boolean) =>
      following ? unfollowExplorer(username) : followExplorer(username),
    onSuccess: async () => {
      // The follow set changes what the Following feed shows and which tab is
      // the server default — drop both caches.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["explorers", username] }),
        queryClient.invalidateQueries({ queryKey: ["social/feed"] }),
      ]);
    },
  });

  const postsQuery = useInfiniteQuery({
    queryKey: ["explorers", username, "posts"],
    queryFn: ({ pageParam }) => fetchExplorerPosts(username, pageParam as string | undefined),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: !!explorerQuery.data,
  });

  const posts = postsQuery.data?.pages.flatMap((p) => p.items) ?? [];
  const explorerData = explorerQuery.data?.explorer;
  const displayName = explorerData?.name ?? explorerData?.username ?? username;

  usePageMeta({
    title: `${displayName} (@${username}) — explorer profile | Atlas`,
    description: explorerData
      ? `${displayName} is level ${explorerData.level} on Atlas with ${explorerData.stats.totalSummits} summits and ${explorerData.stats.totalDaysOut} days out.`
      : `${displayName}'s exploration record on Atlas — collections, achievements and coverage.`,
    canonicalPath: `/explorers/${username}`,
    ogImage: explorerData?.avatarUrl ?? undefined,
  });

  let content: React.ReactNode;

  if (explorerQuery.isLoading || meLoading) {
    content = (
      <div className="r-page" style={{ padding: 28, display: "flex", flexDirection: "column", gap: 20, flex: 1, overflow: "auto" }}>
        <Skel height="220px" />
        <Skel height="100px" />
        <Skel height="160px" />
      </div>
    );
  } else if (explorerQuery.isError || !explorerQuery.data) {
    content = (
      <div style={{ padding: 28, flex: 1, overflow: "auto", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <EmptyState
          icon={<Users size={20} />}
          title="This explorer's profile is private or doesn't exist"
          action={{ label: "Go home", onClick: () => { window.location.href = "/"; } }}
        />
      </div>
    );
  } else {
    const explorer = explorerQuery.data.explorer;
    const bannerSrc = getBannerAsset(explorer.equipped.banner?.metadata.assetKey);

    content = (
      <div style={{ overflow: "auto", flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
        {/* Hero */}
        <div
          className={bannerSrc ? undefined : "atlas-topo"}
          style={{
            position: "relative",
            // Taller header when a banner is equipped so the artwork reads
            // (assets are 1200×280) — mirrors ProfilePage's header.
            padding: bannerSrc ? "96px 28px 24px" : "32px 28px 28px",
            // Clearance so the rarity drop glow fades into the background
            // instead of painting over the content below.
            marginBottom: 24,
            ...(bannerSrc
              ? {
                  backgroundImage: `url("${bannerSrc}")`,
                  backgroundSize: "cover",
                  // Banner art keeps its horizon/detail in the bottom third —
                  // a centered crop on a wide, short header shows only dark sky.
                  backgroundPosition: "center bottom",
                  boxShadow: rarityGlowMd(explorer.equipped.banner?.rarity),
                }
              : {}),
          }}
        >
          {/* Scrim: banner stays unobscured up top; only the text zone at the
              bottom gets protection. Original fade when no banner. */}
          <div
            aria-hidden="true"
            style={{
              position: "absolute",
              inset: 0,
              background: bannerSrc
                ? "linear-gradient(0deg, color-mix(in srgb, var(--bg-app) 82%, transparent) 0%, color-mix(in srgb, var(--bg-app) 35%, transparent) 38%, transparent 68%)"
                : "linear-gradient(0deg, var(--bg-app) 0%, color-mix(in srgb, var(--bg-app) 40%, transparent) 100%)",
            }}
          />
          <div
            className="r-wrap"
            style={{
              position: "relative",
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              gap: 16,
            }}
          >
            <ProfileIdentity
              name={explorer.name ?? explorer.username}
              avatarUrl={explorer.avatarUrl}
              title={explorer.equipped.title}
              badges={explorer.equipped.badges}
              frame={explorer.equipped.frame}
              size="lg"
              subtitle={
                <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--text-muted)" }}>
                  @{explorer.username}
                  {" · "}
                  <span className="tnum">{explorer.followerCount.toLocaleString()}</span>
                  {explorer.followerCount === 1 ? " follower" : " followers"}
                  {" · "}
                  <span className="tnum">{explorer.followingCount.toLocaleString()}</span> following
                </div>
              }
            />
            {me && !explorer.viewer.isSelf && (
              <Button
                variant={explorer.viewer.isFollowing ? "secondary" : "primary"}
                leftIcon={explorer.viewer.isFollowing ? <UserCheck size={14} /> : <UserPlus size={14} />}
                disabled={followMutation.isPending}
                onClick={() => followMutation.mutate(explorer.viewer.isFollowing)}
              >
                {explorer.viewer.isFollowing ? "Following" : "Follow"}
              </Button>
            )}
          </div>
        </div>

        <div style={{ padding: "0 28px 28px", display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Stat row */}
          <div
            className="r-cols-2"
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(5, 1fr)",
              gap: 12,
              background: "var(--surface-card)",
              border: "1px solid var(--border-subtle)",
              borderRadius: "var(--radius-lg)",
              boxShadow: "var(--shadow-sm), var(--ring-top)",
              padding: 18,
            }}
          >
            <StatBlock label="Outdoor score" value={explorer.outdoorScore.toLocaleString()} gold />
            <StatBlock label="Level" value={String(explorer.level)} />
            <StatBlock label="Summits" value={String(explorer.stats.totalSummits)} />
            {/* No distance on public profiles: it is summed over Strava
                activities, which may only be shown to the athlete (Strava
                API Policy 6.1). The API sends 0 for it. */}
            <StatBlock label="Days out" value={String(explorer.stats.totalDaysOut)} />
          </div>

          {/* Recent achievements */}
          {explorer.recentUnlocks.length > 0 && (
            <AtlasPanel title="Recent achievements">
              <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {explorer.recentUnlocks.slice(0, 6).map((u) => (
                  <AchievementBadge
                    key={u.achievementId}
                    title={u.name}
                    description={u.description}
                    tier={u.tier}
                    points={u.points}
                    unlocked
                    icon={achievementIcon(u.iconName, 24)}
                  />
                ))}
              </div>
            </AtlasPanel>
          )}

          {/* Custom lists */}
          {explorer.customCollections.length > 0 && (
            <AtlasPanel title="Custom lists">
              <div className="r-cols-1" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {explorer.customCollections.map((cc) => (
                  <CollectionCard
                    key={cc.id}
                    title={cc.name}
                    type="Custom list"
                    value={cc.completedCount}
                    max={cc.itemCount}
                    icon={<ListChecks size={22} />}
                    onClick={() =>
                      void navigate({
                        to: "/explorers/$username/lists/$id",
                        params: { username, id: cc.id },
                      })
                    }
                  />
                ))}
              </div>
            </AtlasPanel>
          )}

          {/* Posts */}
          <div>
            <p className="eyebrow" style={{ fontSize: 11, marginBottom: 12 }}>Posts</p>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {postsQuery.isLoading ? (
                <>
                  <Skel height="160px" radius="var(--radius-lg)" />
                  <Skel height="160px" radius="var(--radius-lg)" />
                </>
              ) : posts.length === 0 ? (
                <EmptyState icon={<Trophy size={18} />} title="No public posts yet" />
              ) : (
                posts.map((post) => <PostCard key={post.id} post={post} readOnly={!me} />)
              )}

              {postsQuery.hasNextPage && (
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    variant="secondary"
                    onClick={() => void postsQuery.fetchNextPage()}
                    disabled={postsQuery.isFetchingNextPage}
                  >
                    {postsQuery.isFetchingNextPage ? "Loading…" : "Load more"}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (me) {
    return <AppShell>{content}</AppShell>;
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg-app)", display: "flex", flexDirection: "column" }}>
      <GuestTopBar />
      {content}
    </div>
  );
}
